import React from 'react';
import { createTheming, createUseStyles } from 'react-jss';

/* Create a React context which JSS would need to wrap on */
const SurveyThemeContext = React.createContext({});
SurveyThemeContext.displayName = 'SurveyTheme';

/* Use JSS custom hook to create the wrapper */
const theming = createTheming(SurveyThemeContext);

/* Expose the associated hooks with aliases */
export const {
  useTheme: useSurveyTheme,
  ThemeProvider: SurveyThemeProvider,
} = theming;

/**
 * For convenience, expose a custom hook which would carry the theming context wrapper along,
 * as well as injecting style sheet name-space / class name prefix as "sm" by default.
 *
 * Note: One may still utilize the stock `createUseStyles()` hook to construct styles regardless
 * relying on a survey theme or not; however if they do, please keep in mind that the recommended
 * way per JSS documentation is to pass in `theming` context wrapper every time.
 * ( https://cssinjs.org/react-jss?v=v10.5.0#using-custom-theming-context )
 *
 * It is also noteworthy that as of JSS v.10.5.0, there's a strange behavior
 * when calling `createUseStyles()`, if the first parameter is an object (en lieu a function),
 * it would ignore the `theming` context wrapper coming along in the same call.
 * Considering the caller of this hook would very much acquire the theming data on the nested
 * function values, we're hereby enforce the behavior which would also name-space the style sheet.
 */
export const createSurveyStyles = (styles, opts = {}) => {
  /* force to pass a function in and ignore a linter error to avoid JSS warnings */
  /* eslint-disable-next-line no-unused-vars */
  const _styles = typeof styles === 'function' ? styles : theme => styles;

  /* call the higher-order function in JSS with the normalized signature */
  return createUseStyles(_styles, { theming, name: 'sm', ...opts });
};

/**
 * The context wrapper constructed by JSS `createTheming()` hook.
 * Note: `theming.context` points to the same SurveyThemeContext we just instantiated
 */
export default theming;
